import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState, useEffect, useMemo} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {
  getSimplifiedDataEntryResponse,
  isCrossModelValidationVerified,
} from 'notebook/ai-tools/flexible-data-entry/CrossModelValidation';
import {updateResultsWithEdits} from 'notebook/ai-tools/flexible-data-entry/FlexibleDataEntryResultEdits';
import ReviewAndConfirmResultsStep, {
  getReviewAndConfirmResultsModalButtons,
} from 'notebook/ai-tools/flexible-data-entry/ReviewAndConfirmResultsStep';
import SelectTablesAndFilesStep, {
  getSelectTablesAndFilesModalButtons,
} from 'notebook/ai-tools/flexible-data-entry/SelectTablesAndFilesStep';
import {FlexibleDataEntryExperience} from 'notebook/ai-tools/NotebookAIToolsConstants';
import {
  FillStructuredTablePartialResponse,
  fillStructuredTableUsingAI,
  FillStructuredTableUsingAIResponse,
} from 'notebook/ai-tools/NotebookAIToolsDAO';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {Row} from 'notebook/tables/TableConstants';
import {getEntryOrFileIdKey} from 'notebook/util/analytics/FileAnalyticsUtils';
import {FromServerNoteLinkedFile} from 'notebook/util/NoteUtilConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import useIsNotUnmounted from 'ui-library/hooks/useIsNotUnmounted';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepNavVariant, StepNavSize, StepItemStatus} from 'ui-library/StepNav';
import {getUserMessage} from 'util/Net';

import {convertPromptToFile} from './addStructuredTableRowsUsingAI';
import {getEarlyDataEntryResponse} from './CrossModelValidation';
import {useFileUploads, useFileUpload} from './FileUploads';
import {useFlexibleDataEntryResultEditsControls} from './FlexibleDataEntryResultEdits';
import SuggestClarificationsStep, {
  getSuggestClarificationsModalButtons,
  getUpdatedPromptTextWithSuggestedClarifications,
  SuggestedClarificationsStateType,
  useAIDataEntrySuggestedClarificationsControls,
} from './SuggestClarificationsStep';

enum FlexibleDataEntryStep {
  SELECT_TABLES_AND_FILES = 'SELECT_TABLES_AND_FILES',
  SUGGEST_CLARIFICATIONS = 'SUGGEST_CLARIFICATIONS',
  REVIEW_AND_CONFIRM_RESULTS = 'REVIEW_AND_CONFIRM_RESULTS',
}

export interface AIFillStructuredTableModalResult {
  newRows: {[tableName: string]: Array<Row>};
  promptText: string;
  fileNoteLinkedFilesPromise: Promise<Array<FromServerNoteLinkedFile>>;
  promptNoteLinkedFilePromise: Promise<FromServerNoteLinkedFile>;
}

export interface AIFillStructuredTableModalProps {
  entryId: string;
  structuredTables: Array<StructuredTable<unknown, unknown>>;
  onRequestClose: () => void;
  onRequestSubmit: (result: AIFillStructuredTableModalResult) => void;
  initialPromptText?: string;
  initialSelectedTableName?: string;
}

/**
 * Modal for the Data Entry Agent feature, allowing users to fill a structured table based on one
 * or more files or other context.
 *
 * This component is a multi-pane modal. All state management and analytics are handled by this layer,
 * and the visuals and interactions are handled by a child component/file per pane.
 */
export default function AIFillStructuredTableModal({
  entryId,
  structuredTables,
  onRequestSubmit,
  onRequestClose,
  initialPromptText = '',
  initialSelectedTableName,
}: AIFillStructuredTableModalProps): JSX.Element {
  assert(structuredTables.length > 0, 'At least one structured table must be provided');
  const isMounted = useIsNotUnmounted();
  const [currentStep, setCurrentStep] = useState<FlexibleDataEntryStep>(
    FlexibleDataEntryStep.SELECT_TABLES_AND_FILES
  );

  // State specified by first step: add files and instructions.
  const {uploadFile: uploadPromptFile, waitForNoteLinkedFile: waitForPromptNoteLinkedFile} = useFileUpload();
  const {
    selectedFiles,
    setSelectedFiles,
    errorMessage: fileUploadsError,
    waitForFileUploads,
    waitForNoteLinkedFiles,
  } = useFileUploads();
  const [editedRowsByTable, setEditedRowsByTable] = useState<Record<string, Array<Row>>>({});

  const {
    suggestedClarificationsState,
    setSuggestedClarificationsState,
    suggestClarifications,
    clearSuggestedClarifications,
  } = useAIDataEntrySuggestedClarificationsControls(initialPromptText);
  const [selectedTableNames, setSelectedTableNames] = React.useState<Array<string>>(
    initialSelectedTableName ? [initialSelectedTableName] : []
  );
  const selectedStructuredTables = useMemo(
    () => structuredTables.filter((table) => table.name && selectedTableNames.includes(table.name)),
    [structuredTables, selectedTableNames]
  );
  const {modelEdits, handleCellEdit, clearModelEdits} = useFlexibleDataEntryResultEditsControls(
    structuredTables.map((table) => table.name) // Provide all table names to avoid re-rendering when the selected table names change.
  );

  // Server-provided state loaded by second step: review and confirm results.

  const [fillResult, setFillResult] = useState<FillStructuredTableUsingAIResponse | null>(null);
  const [selectedResultIndex, setSelectedResultIndex] = useState<number>(0);

  // Server-provided message when the whole operation fails.
  const [serverErrorMessage, setServerErrorMessage] = useState<string | null>(null);

  // Store the submitted prompt text to save after submission.
  const [submittedPromptText, setSubmittedPromptText] = useState<string | null>(null);
  // Server-provided status for the fill operation.
  const [fillStructuredTableStatus, setFillStructuredTableStatus] =
    useState<FillStructuredTablePartialResponse | null>(null);
  // Index of the table currently being displayed.
  const [displayedTableIndex, setDisplayedTableIndex] = useState(0);

  // Extract necessary metadata information from current state.

  // Table order is provided by the LLM after the clarifications are suggested. This dictates the order in
  // which tables are displayed for the user to review and fill.
  const tableOrder =
    suggestedClarificationsState.type === SuggestedClarificationsStateType.SUCCESS
      ? suggestedClarificationsState?.response?.tableOrder
      : undefined;
  // Is the current table being displayed the last table in the order?
  const isLastTable = Boolean(
    structuredTables.length === 1 ||
      (tableOrder ?? selectedStructuredTables).length - 1 === displayedTableIndex
  );
  // Table name of the table currently being displayed.
  const displayedStructuredTableName =
    structuredTables.length > 1 && tableOrder && displayedTableIndex < tableOrder.length
      ? tableOrder[displayedTableIndex]
      : selectedStructuredTables[displayedTableIndex]?.name;
  // The table currently being displayed.
  const displayedStructuredTable = displayedStructuredTableName
    ? structuredTables.find((table) => table.name === displayedStructuredTableName)
    : undefined;
  // The model result for the current table being displayed.
  const displayedResult = fillResult?.runResults[selectedResultIndex];
  // Number of files selected.
  const numFiles = selectedFiles.length;

  // Set up control variables derived from current state.
  // True if no new rows.
  const isSubmitted = currentStep === FlexibleDataEntryStep.REVIEW_AND_CONFIRM_RESULTS;
  // True if the current table has no additional rows to fill.
  const hasNoRowsToFill =
    displayedResult?.newRows && Object.values(displayedResult.newRows).every((rows) => rows.length === 0);
  // True if the modal reached a terminal state (i.e. failure / success, but not running).
  const isCompleted =
    Boolean(hasNoRowsToFill) || fillResult != null || serverErrorMessage != null || fileUploadsError != null;
  // True if the primary run configuration returned a nonempty response.
  const wasSuccessful =
    !fileUploadsError && // even if it was technically successful, a file upload for notebook is blocking.
    !hasNoRowsToFill &&
    displayedResult?.newRows != null &&
    displayedStructuredTableName != null &&
    Object.values(displayedResult.newRows).some((rows) => rows.length > 0);
  const wasVerified = wasSuccessful && fillResult != null && isCrossModelValidationVerified(fillResult);

  useEffect(() => {
    const fileIdKey = getEntryOrFileIdKey(entryId, true);
    trackEvent(ELN_ANALYTICS_ACTIONS.OPENED_FLEXIBLE_DATA_ENTRY_MODAL, {
      [fileIdKey]: entryId,
      experience: FlexibleDataEntryExperience.MODAL,
    });
  }, [entryId]);

  const handleSubmitPrompt = useCallback(async () => {
    if (isSubmitted) {
      return;
    }
    setCurrentStep(FlexibleDataEntryStep.REVIEW_AND_CONFIRM_RESULTS);
    const startTime = performance.now();
    const updatedPromptText = getUpdatedPromptTextWithSuggestedClarifications(
      suggestedClarificationsState.suggestedPrompt,
      selectedStructuredTables,
      suggestedClarificationsState
    );

    // Start prompt upload
    uploadPromptFile(convertPromptToFile(updatedPromptText));

    trackEvent(ELN_ANALYTICS_ACTIONS.SUBMITTED_FLEXIBLE_DATA_ENTRY_REQUEST, {
      numFiles,
      entryId,
      experience: FlexibleDataEntryExperience.MODAL,
      isMultiTable: selectedStructuredTables.length > 1,
    });
    let fullResponse: FillStructuredTableUsingAIResponse;
    // Track whether we stopped early with a result due to early verification.
    // IMPORTANT: After this is set, no further events should update component state. The user may
    // have clicked "start over" and started another run, and we setting state would disrupt that.
    // To support further events after early return, we should rework our approach to state and/or
    // implement some form of cancellation on "start over".
    let stoppedEarly = false;

    function handlePartialResponse(partialResponse: FillStructuredTablePartialResponse): void {
      if (stoppedEarly) {
        return;
      }
      setFillStructuredTableStatus(partialResponse);
      const earlyResponse = getEarlyDataEntryResponse(partialResponse);
      if (earlyResponse) {
        stoppedEarly = true;
        handleResponse(earlyResponse);
        trackEvent(ELN_ANALYTICS_ACTIONS.RECEIVED_FLEXIBLE_DATA_ENTRY_EARLY_RESULT, {
          isModalOpen: isMounted.current,
          numFiles,
          entryId,
          durationSeconds: (performance.now() - startTime) / 1000,
          isMultiTable: selectedStructuredTables.length > 1,
        });
      }
    }

    function handleResponse(response: FillStructuredTableUsingAIResponse): void {
      setSubmittedPromptText(updatedPromptText);
      setFillResult(response);
    }

    try {
      const files = await waitForFileUploads();
      fullResponse = await fillStructuredTableUsingAI(
        entryId,
        selectedStructuredTables.map((table) => table.name),
        files,
        updatedPromptText,
        handlePartialResponse
      );
    } catch (e) {
      setServerErrorMessage(getUserMessage(e) ?? 'An error occurred.');
      throw e;
    }
    const simplifiedResponse = getSimplifiedDataEntryResponse(fullResponse);

    if (!stoppedEarly) {
      // For display purposes, for now we show results as if there were just two runs, picking the
      // matching run if there is one.
      handleResponse(simplifiedResponse);
    }

    const responseWasSuccessful = simplifiedResponse.runResults.some((result) =>
      Object.values(result.newRows).some((rows) => rows.length > 0)
    );
    trackEvent(ELN_ANALYTICS_ACTIONS.RECEIVED_FLEXIBLE_DATA_ENTRY_RESULT, {
      isModalOpen: isMounted.current,
      wasSuccessful: responseWasSuccessful,
      wasVerified: isCrossModelValidationVerified(fullResponse),
      numFiles,
      entryId,
      durationSeconds: (performance.now() - startTime) / 1000,
      isMultiTable: selectedStructuredTables.length > 1,
      experience: FlexibleDataEntryExperience.MODAL,
    });
  }, [
    entryId,
    isMounted,
    isSubmitted,
    numFiles,
    suggestedClarificationsState,
    selectedStructuredTables,
    waitForFileUploads,
    uploadPromptFile,
  ]);

  const handleClose = useCallback(() => {
    const fileIdKey = getEntryOrFileIdKey(entryId, true);
    trackEvent(ELN_ANALYTICS_ACTIONS.CLOSED_FLEXIBLE_DATA_ENTRY_MODAL, {
      isSubmitted,
      isCompleted,
      numFiles,
      [fileIdKey]: entryId,
      experience: FlexibleDataEntryExperience.MODAL,
    });
    onRequestClose();
  }, [entryId, isCompleted, isSubmitted, numFiles, onRequestClose]);

  const handleInsert = useCallback(() => {
    if (!wasSuccessful || displayedResult == null) {
      handleClose();
    } else {
      assert(fillResult != null, 'fillResult must be defined');
      assert(displayedStructuredTableName != null, 'displayedStructuredTableName must be defined');

      const editedRows = updateResultsWithEdits(
        displayedResult.newRows[displayedStructuredTableName],
        modelEdits,
        selectedResultIndex,
        displayedStructuredTableName
      );
      const newEditedRows = {
        ...editedRowsByTable,
        [displayedStructuredTableName]: editedRows,
      };
      setEditedRowsByTable(newEditedRows);

      if (!isLastTable) {
        setDisplayedTableIndex(displayedTableIndex + 1);
        return;
      }

      trackEvent(ELN_ANALYTICS_ACTIONS.ACCEPTED_FLEXIBLE_DATA_ENTRY_RESULT, {
        numFiles,
        wasVerified: isCrossModelValidationVerified(fillResult),
        entryId,
        experience: FlexibleDataEntryExperience.MODAL,
        isMultiTable: selectedStructuredTables.length > 1,
      });

      onRequestSubmit({
        newRows: newEditedRows,
        promptText: submittedPromptText ?? '',
        fileNoteLinkedFilesPromise: waitForNoteLinkedFiles(),
        // Invariant: handleInsert is only called after onRequestSubmit is called,
        // which means the uploadPromptFile was already called.
        promptNoteLinkedFilePromise: waitForPromptNoteLinkedFile(),
      });
    }
  }, [
    wasSuccessful,
    displayedResult,
    handleClose,
    fillResult,
    displayedStructuredTableName,
    modelEdits,
    selectedResultIndex,
    editedRowsByTable,
    isLastTable,
    numFiles,
    entryId,
    selectedStructuredTables,
    onRequestSubmit,
    submittedPromptText,
    waitForNoteLinkedFiles,
    waitForPromptNoteLinkedFile,
    displayedTableIndex,
  ]);

  // Step 1 → Step 2
  const handleNextToClarifications = useCallback(async () => {
    setCurrentStep(FlexibleDataEntryStep.SUGGEST_CLARIFICATIONS);
    await suggestClarifications(
      entryId,
      selectedStructuredTables.map((table) => table.name),
      selectedFiles,
      initialPromptText,
      true // this is the initial suggestion, so generate both the prompt and the example row
    );
  }, [entryId, selectedStructuredTables, selectedFiles, suggestClarifications, initialPromptText]);

  // Step 2 → Step 1
  const handleGoBackFromClarifications = useCallback(() => {
    setCurrentStep(FlexibleDataEntryStep.SELECT_TABLES_AND_FILES);
    clearSuggestedClarifications();
  }, [clearSuggestedClarifications]);

  // Step 2 → Step 3
  const handleSubmitFromClarifications = useCallback(() => {
    handleSubmitPrompt();
  }, [handleSubmitPrompt]);

  // Step 3 → Step 2
  const handleGoBackFromResults = useCallback(() => {
    if (displayedTableIndex > 0) {
      setDisplayedTableIndex(displayedTableIndex - 1);
      return;
    }
    trackEvent(ELN_ANALYTICS_ACTIONS.STARTED_OVER_FLEXIBLE_DATA_ENTRY, {
      wasSuccessful,
      wasVerified,
      numFiles,
      entryId,
      experience: FlexibleDataEntryExperience.MODAL,
    });
    setCurrentStep(FlexibleDataEntryStep.SUGGEST_CLARIFICATIONS);
    // Clear results state but preserve clarifications state
    setFillResult(null);
    setSelectedResultIndex(0);
    clearModelEdits();
    setServerErrorMessage(null);
    setSubmittedPromptText(null);
    setFillStructuredTableStatus(null);
  }, [entryId, numFiles, wasSuccessful, wasVerified, clearModelEdits, displayedTableIndex]);

  // Regenerate example row with current prompt text
  const handleRegenerateExampleRow = useCallback(() => {
    suggestClarifications(
      entryId,
      selectedStructuredTables.map((table) => table.name),
      selectedFiles,
      suggestedClarificationsState.suggestedPrompt,
      false // not the initial suggestion, so don't regenerate prompt
    );
  }, [entryId, selectedStructuredTables, selectedFiles, suggestedClarificationsState, suggestClarifications]);

  let buttons: Array<ModalButton>;
  if (currentStep === FlexibleDataEntryStep.SELECT_TABLES_AND_FILES) {
    buttons = getSelectTablesAndFilesModalButtons({
      handleNext: handleNextToClarifications,
      isDisabled: selectedStructuredTables.length === 0 || fileUploadsError != null,
    });
  } else if (currentStep === FlexibleDataEntryStep.SUGGEST_CLARIFICATIONS) {
    buttons = getSuggestClarificationsModalButtons({
      handleSubmit: handleSubmitFromClarifications,
      handleGoBack: handleGoBackFromClarifications,
      isUpdating: suggestedClarificationsState.type === SuggestedClarificationsStateType.UPDATING,
    });
  } else {
    buttons = getReviewAndConfirmResultsModalButtons(
      wasVerified,
      wasSuccessful,
      isCompleted,
      handleGoBackFromResults,
      handleInsert,
      displayedTableIndex,
      selectedStructuredTables.length
    );
  }

  const handleCellEditForDisplayedTable = useCallback(
    (modelIndex: number, rowIndex: number, columnIndex: number, newValue: string | null) => {
      assert(displayedStructuredTableName != null, 'displayedStructuredTableName must be defined');
      handleCellEdit(modelIndex, rowIndex, columnIndex, newValue, displayedStructuredTableName);
    },
    [handleCellEdit, displayedStructuredTableName]
  );

  return (
    <ExpandableModal
      title="Data Entry Agent"
      large={true}
      onRequestClose={handleClose}
      buttons={buttons}
      closeableOnEscapeKey={false}
    >
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text={
          <span>
            This feature is powered by AI, and may make mistakes. See the{' '}
            <a
              href="https://help.benchling.com/hc/en-us/articles/34900709662989"
              target="_blank"
              rel="noopener noreferrer"
            >
              help article
            </a>{' '}
            for more information.{' '}
          </span>
        }
      />
      <div className={css(styles.stepNav)}>
        <StepNav<FlexibleDataEntryStep>
          steps={[
            {key: FlexibleDataEntryStep.SELECT_TABLES_AND_FILES, title: 'Select tables and files'},
            {key: FlexibleDataEntryStep.SUGGEST_CLARIFICATIONS, title: 'Confirm suggested instructions'},
            {key: FlexibleDataEntryStep.REVIEW_AND_CONFIRM_RESULTS, title: 'Review and confirm results'},
          ]}
          statusByStep={{
            [FlexibleDataEntryStep.SELECT_TABLES_AND_FILES]:
              currentStep !== FlexibleDataEntryStep.SELECT_TABLES_AND_FILES
                ? StepItemStatus.DONE
                : StepItemStatus.UNVISITED,
            [FlexibleDataEntryStep.SUGGEST_CLARIFICATIONS]:
              currentStep === FlexibleDataEntryStep.REVIEW_AND_CONFIRM_RESULTS
                ? StepItemStatus.DONE
                : StepItemStatus.UNVISITED,
            [FlexibleDataEntryStep.REVIEW_AND_CONFIRM_RESULTS]: StepItemStatus.UNVISITED,
          }}
          size={StepNavSize.SMALL}
          variant={StepNavVariant.FLAT}
          currentStepKey={currentStep}
          onUpdateCurrentStep={setCurrentStep}
          // Disable clicking to navigate since we want it to be clear that results are discarded.
          disabled={true}
        />
      </div>
      {currentStep === FlexibleDataEntryStep.SELECT_TABLES_AND_FILES && (
        <SelectTablesAndFilesStep
          selectedFiles={selectedFiles}
          onSetSelectedFiles={setSelectedFiles}
          structuredTables={structuredTables}
          selectedTableNames={selectedTableNames}
          setSelectedTableNames={setSelectedTableNames}
          errorMessage={fileUploadsError}
        />
      )}
      {currentStep === FlexibleDataEntryStep.SUGGEST_CLARIFICATIONS && (
        <SuggestClarificationsStep
          suggestedClarificationsState={suggestedClarificationsState}
          structuredTables={selectedStructuredTables}
          onUpdateSuggestedClarificationsState={setSuggestedClarificationsState}
          onRegenerateExampleRow={handleRegenerateExampleRow}
        />
      )}
      {currentStep === FlexibleDataEntryStep.REVIEW_AND_CONFIRM_RESULTS && (
        <ReviewAndConfirmResultsStep
          serverErrorMessage={serverErrorMessage ?? fileUploadsError}
          wasSuccessful={wasSuccessful}
          fillResult={fillResult}
          selectedResultIndex={selectedResultIndex}
          onSelectResultIndex={setSelectedResultIndex}
          structuredTable={displayedStructuredTable}
          modelEdits={modelEdits}
          onCellEdit={handleCellEditForDisplayedTable}
          fillStructuredTableStatus={fillStructuredTableStatus}
        />
      )}
    </ExpandableModal>
  );
}

const styles = StyleSheet.create({
  stepNav: {
    marginTop: 15,
    marginBottom: 15,
  },
});
